import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import api from "@/lib/api";

import LandingPage from "@/pages/Landing";
import ReviewPage from "@/pages/customer/ReviewPage";
import PaymentSuccess from "@/pages/public/PaymentSuccess";
import PaymentCancelled from "@/pages/public/PaymentCancelled";
import ContactUs from "@/pages/public/ContactUs";
import Support from "@/pages/public/Support";
import DemoGoogleReviewPage from "@/pages/public/DemoGoogleReviewPage";

// ---------------------------------------------------------------------------
// Admin bundle — lazy-loaded so its JS/CSS never ships with the public site.
// Each of these becomes its own webpack chunk; only `/admin/*` requests fetch it.
// ---------------------------------------------------------------------------
const AuthCallback = lazy(() => import("@/pages/admin/AuthCallback"));
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const AdminLayout = lazy(() => import("@/components/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const BusinessList = lazy(() => import("@/pages/admin/BusinessList"));
const BusinessForm = lazy(() => import("@/pages/admin/BusinessForm"));
const BusinessProfile = lazy(() => import("@/pages/admin/BusinessProfile"));
const Onboarding = lazy(() => import("@/pages/admin/Onboarding"));
const Commissions = lazy(() => import("@/pages/admin/Commissions"));
const Reporting = lazy(() => import("@/pages/admin/Reporting"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const Users = lazy(() => import("@/pages/admin/Users"));
const Inquiries = lazy(() => import("@/pages/admin/Inquiries"));

const AdminLoader = () => (
  <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">
    Loading admin…
  </div>
);

function ProtectedAdmin({ children }) {
  const [state, setState] = useState({ checked: false, ok: false });
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setState({ checked: true, ok: true });
      return;
    }
    api.get("/auth/me").then(() => setState({ checked: true, ok: true }))
      .catch(() => setState({ checked: true, ok: false }));
  }, []);
  if (!state.checked) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Checking session...</div>;
  }
  if (!state.ok) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect session_id in hash synchronously during render before ProtectedRoute logic runs
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<AdminLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Routes>
      {/* Public routes — no admin code loaded */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/r/:slug" element={<ReviewPage />} />
      <Route path="/success" element={<PaymentSuccess />} />
      <Route path="/cancelled" element={<PaymentCancelled />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/support" element={<Support />} />
      <Route path="/demo/google" element={<DemoGoogleReviewPage />} />

      {/* Admin — behind a Suspense boundary so its chunk loads on demand */}
      <Route path="/admin/login" element={
        <Suspense fallback={<AdminLoader />}><AdminLogin /></Suspense>
      } />
      <Route element={
        <Suspense fallback={<AdminLoader />}>
          <ProtectedAdmin><AdminLayout /></ProtectedAdmin>
        </Suspense>
      }>
        <Route path="/admin" element={<Suspense fallback={<AdminLoader />}><Dashboard /></Suspense>} />
        <Route path="/admin/businesses" element={<Suspense fallback={<AdminLoader />}><BusinessList /></Suspense>} />
        <Route path="/admin/businesses/new" element={<Suspense fallback={<AdminLoader />}><BusinessForm /></Suspense>} />
        <Route path="/admin/businesses/:id" element={<Suspense fallback={<AdminLoader />}><BusinessProfile /></Suspense>} />
        <Route path="/admin/onboarding/:id" element={<Suspense fallback={<AdminLoader />}><Onboarding /></Suspense>} />
        <Route path="/admin/commissions" element={<Suspense fallback={<AdminLoader />}><Commissions /></Suspense>} />
        <Route path="/admin/reporting" element={<Suspense fallback={<AdminLoader />}><Reporting /></Suspense>} />
        <Route path="/admin/users" element={<Suspense fallback={<AdminLoader />}><Users /></Suspense>} />
        <Route path="/admin/inquiries" element={<Suspense fallback={<AdminLoader />}><Inquiries /></Suspense>} />
        <Route path="/admin/settings" element={<Suspense fallback={<AdminLoader />}><Settings /></Suspense>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}
